/**
 * Speed-limits slice — operator-chosen speed caps, clamped to the robot's own ceiling.
 *
 * The robot ceiling itself is NOT copied here — it's read live from the sibling
 * `robotConfig` slice (same module, injected at construction) on every access, so
 * there is exactly one stored copy of `RobotLimits` in the whole app (`robotConfig`).
 * This slice only owns the part robotConfig doesn't have: the operator's chosen caps.
 *
 * Framework-free, same shape as the other slices (`zustand/vanilla` store).
 */

import { createStore } from "./store";
import type { StoreApi } from "./store";
import { isAckermannLimits } from "./robot-config";
import type { RobotConfigSlice, RobotLimits } from "./robot-config";
import type { MotionProfile, SpeedAxis, VelocityDemand } from "../../types/commands";

export interface UserLimits {
  vx: number | null;
  vy: number | null;
  vyaw: number | null;
  /** Ackermann/rover cap — only ever set once the robot ceiling includes
   * max_steer_angle (see resync() below); stays undefined for a holonomic robot. */
  steer_angle?: number | null;
}

const AXIS_TO_LIMIT: Record<SpeedAxis, keyof RobotLimits> = {
  vx: "max_vx",
  vy: "max_vy",
  vyaw: "max_vyaw",
  steer_angle: "max_steer_angle",
};

function isUsableMax(max: number | null | undefined): max is number {
  return max != null && Number.isFinite(max) && max > 0;
}

function clampToMax(value: number, max: number): number {
  return Math.max(0, Math.min(max, value));
}

/** Seed used when the robot hasn't sent a ceiling for an axis at all — the UI still
 * needs a starting cap, so this doubles as the default before the operator adjusts the
 * (manually-set) slider ceiling. Matches the "50% of ceiling" convention used elsewhere. */
const UNCONFIRMED_AXIS_SEED = 0.5;

/**
 * @param value start velocity for an axis
 * @param max   robot ceiling for that axis
 * @returns the starting user cap — clamped to the robot ceiling when there is one,
 *   otherwise the raw start velocity (or a flat seed) so the axis stays configurable
 */
function resolveStartVelocity(
  value: number | null | undefined,
  max: number | null | undefined
): number {
  if (!isUsableMax(max)) {
    return value != null && Number.isFinite(value) ? Math.max(0, value) : UNCONFIRMED_AXIS_SEED;
  }
  if (value == null || !Number.isFinite(value)) return max * 0.5;
  return clampToMax(value, max);
}

/**
 * Keep operator-chosen caps across rejoin; seed from start_velocity only when unset.
 * Without a robot ceiling for the axis, an existing cap is kept as-is (not wiped to
 * null) — the operator can still set/keep a limit, it's just not clamped to hardware.
 */
function resolveUserLimit(
  existing: number | null | undefined,
  startValue: number | null | undefined,
  max: number | null | undefined
): number {
  if (existing != null && Number.isFinite(existing)) {
    return isUsableMax(max) ? clampToMax(existing, max) : existing;
  }
  return resolveStartVelocity(startValue, max);
}

export interface SpeedLimitsSlice extends Pick<
  StoreApi<UserLimits>,
  "getState" | "getInitialState" | "subscribe"
> {
  setUserLimit(axis: SpeedAxis, value: number): void;
  /** Slider caps in SI (m/s, rad/s) for wire commands; falls back to the robot ceiling. */
  getUserSpeedLimits(): Record<SpeedAxis, number>;
  /** Map normalized stick demand (−1…1) to SI velocities using slider caps. */
  scaleNormToSi(norm: VelocityDemand): Record<SpeedAxis, number>;
  /** "ackermann" once the robot ceiling includes max_steer_angle, "holonomic"
   * otherwise (including before any robot-config has arrived) — read live off the
   * sibling robotConfig slice, same as the rest of this file's robot-ceiling access. */
  getMotionProfile(): MotionProfile;
  reset(): void;
}

/**
 * One instance per `createRobotState()` call — never a module-level global, so
 * multiple robot-state instances (e.g. split-screen driving several robots) never
 * share operator caps.
 */
export function createSpeedLimitsSlice(robotConfig: RobotConfigSlice): SpeedLimitsSlice {
  const store = createStore<UserLimits>(() => ({ vx: null, vy: null, vyaw: null }));
  let lastKey = "";

  // Resync operator caps whenever robotConfig settles on a fresh robot-config payload —
  // replaces an external push (`syncRobotConfig`) with an internal subscription, so
  // there is no second copy of the robot ceiling to keep in sync from outside.
  const resync = (): void => {
    const { status, config } = robotConfig.getState();
    if (status !== "ready" || !config?.limits) return;
    const key = JSON.stringify({ limits: config.limits, start_velocity: config.start_velocity });
    if (key === lastKey) return;
    lastKey = key;
    const cur = store.getState();
    const next: UserLimits = {
      vx: resolveUserLimit(cur.vx, config.start_velocity?.vx, config.limits.max_vx),
      vy: resolveUserLimit(cur.vy, config.start_velocity?.vy, config.limits.max_vy),
      vyaw: resolveUserLimit(cur.vyaw, config.start_velocity?.vyaw, config.limits.max_vyaw),
    };
    // Only a rover's robot-config carries a genuinely usable max_steer_angle — a
    // holonomic robot's UserLimits keeps `steer_angle` absent rather than seeding a
    // cap for an axis that never reaches the wire (see teleop-commands'
    // MotionStream). isAckermannLimits (not a bare `!= null`) so a dog that happens
    // to send `max_steer_angle: 0` as an explicit "not applicable" placeholder still
    // resolves as holonomic here.
    if (isAckermannLimits(config.limits)) {
      next.steer_angle = resolveUserLimit(
        cur.steer_angle,
        config.start_velocity?.steer_angle,
        config.limits.max_steer_angle
      );
    }
    store.setState(next, true);
  };
  robotConfig.subscribe(resync);

  // Plain closures (not object-literal methods) on purpose — like commands.limits used
  // to be, these get passed around detached from their owning object (e.g. as a bare
  // prop), so they must not rely on `this`.
  const getUserSpeedLimits = (): Record<SpeedAxis, number> => {
    const user = store.getState();
    const robot = robotConfig.getLimits();
    return {
      vx: user.vx ?? robot?.max_vx ?? 1,
      vy: user.vy ?? robot?.max_vy ?? 1,
      vyaw: user.vyaw ?? robot?.max_vyaw ?? 1,
      steer_angle: user.steer_angle ?? robot?.max_steer_angle ?? 1,
    };
  };

  const scaleNormToSi = (norm: VelocityDemand): Record<SpeedAxis, number> => {
    const lim = getUserSpeedLimits();
    return {
      vx: (norm.vx ?? 0) * lim.vx,
      vy: (norm.vy ?? 0) * lim.vy,
      vyaw: (norm.vyaw ?? 0) * lim.vyaw,
      // A rover has no independent lateral axis — its steering demand rides the
      // shared input domain's `vy` (see GamepadWidget/teleop-input) unless a caller
      // already set `steer_angle` explicitly.
      steer_angle: (norm.steer_angle ?? norm.vy ?? 0) * lim.steer_angle,
    };
  };

  const setUserLimit = (axis: SpeedAxis, value: number): void => {
    if (!Number.isFinite(value)) return;
    const robotMax = robotConfig.getLimits()?.[AXIS_TO_LIMIT[axis]];
    // Without a robot ceiling for this axis there's nothing to clamp to — the caller
    // (the slider's own manually-set ceiling) is the only bound, so just floor at 0.
    const next = isUsableMax(robotMax) ? clampToMax(value, robotMax) : Math.max(0, value);
    store.setState({ ...store.getState(), [axis]: next }, true);
  };

  const getMotionProfile = (): MotionProfile =>
    isAckermannLimits(robotConfig.getLimits()) ? "ackermann" : "holonomic";

  const reset = (): void => {
    lastKey = "";
    store.setState({ vx: null, vy: null, vyaw: null }, true);
  };

  return {
    getState: store.getState,
    getInitialState: store.getInitialState,
    subscribe: store.subscribe,
    setUserLimit,
    getUserSpeedLimits,
    scaleNormToSi,
    getMotionProfile,
    reset,
  };
}
